import React from 'react'
import { Box, Divider, useMediaQuery, useTheme } from '@mui/material'
import TestIds from '../../../testIds'
import { formatPrice, saveScrollYPosition } from '../../../util/helpers'
import StatusChip from '../../../components/StatusChip'
import { ItemContainer, ItemLabel, ItemValue } from '../../../components/styled'
import { DeliveryMode, SalesOrder } from '../storage/types'
import Card, { CardAction, CardTitle } from '../../../components/Card'
import { useLocation, useNavigate } from 'react-router-dom'
import { mapStatus } from '../utils/getOrderStatus'
import { SideContainer } from '../../products/components/ProductCard/ProductCard'
import dayjs from 'dayjs'
import getOrderTypeFromDeliveryMode from '../utils/getOrderType'
import { ScrollYPositionKeys } from '../../../util/consts'
import { calculateOrderDiscount } from '../../../util/calculateEditedPriceDiscount'
import {
  PriceWrapper,
  TopContainer,
  CardTitleWrapper,
  StatusWrapper,
  BottomContainer,
} from './OrderCardComponents'

interface OrderCardProps {
  data: SalesOrder
}

const OrderCard = ({ data }: OrderCardProps) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'))

  const orderDetailsNavigate = (
    e: React.MouseEvent<HTMLElement, MouseEvent> | null,
    path: string,
    salesId: string,
  ) => {
    e?.preventDefault()

    const sessionStorageKey = ScrollYPositionKeys.Orders
    if (pathname === '/orders') saveScrollYPosition({ sessionStorageKey })
    else saveScrollYPosition({ sessionStorageKey, position: 0 })

    navigate(`${path}${salesId}`, {
      state: {
        salesId,
      },
    })
  }

  const renderCustomerRef = React.useMemo(
    () =>
      data.customerReference && (
        <Box sx={{ mt: isSmall ? 0 : 1 }}>
          <ItemContainer className={isSmall ? 'flex-direction-row' : 'flex-direction-col'}>
            <ItemLabel variant='body2' align={isSmall ? 'left' : 'right'}>
              Your reference:
            </ItemLabel>
            <ItemValue
              align={isSmall ? 'left' : 'right'}
              variant={isSmall ? 'body2' : 'h4'}
              fontWeight={isSmall ? 400 : 700}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 300,
              }}
              data-testid={TestIds.feat.orders.card.customerRef}
            >
              {data.customerReference}
            </ItemValue>
          </ItemContainer>
        </Box>
      ),
    [isSmall, data.customerReference],
  )

  const renderOrderPlaced = React.useMemo(() => {
    const renderedItem = (
      <ItemContainer wrap={isSmall}>
        <ItemLabel
          variant={isSmall ? 'body2' : 'caption'}
          align={isSmall ? 'left' : 'right'}
          sx={{ textTransform: isSmall ? 'none' : 'uppercase' }}
        >
          Order placed{isSmall && ':'}
        </ItemLabel>
        <ItemValue
          variant={isSmall ? 'body2' : 'caption'}
          align={isSmall ? 'left' : 'right'}
          data-testid={TestIds.feat.orders.card.placedDate}
        >
          {dayjs(data.createdAt).format('DD MMM YYYY')}
        </ItemValue>
      </ItemContainer>
    )
    if (isSmall) return renderedItem
    return <SideContainer sx={{ m: 0, ml: 'auto' }}>{renderedItem}</SideContainer>
  }, [isSmall, data.createdAt])

  const renderOrderDetails = React.useMemo(() => {
    const variant = isSmall ? 'body2' : 'h4'
    const totalCost = (
      <ItemContainer wrap={isSmall}>
        <ItemLabel variant='body2'>Total cost:</ItemLabel>
        <ItemValue
          variant={variant}
          component='p'
          color={isSmall ? 'grey700' : 'grey800'}
          fontWeight={isSmall ? 400 : 700}
          data-testid={TestIds.feat.orders.card.totalCost}
        >
          {formatPrice(data.totalPrice)}
        </ItemValue>
      </ItemContainer>
    )
    const savings = (
      <ItemContainer wrap={isSmall}>
        <ItemLabel variant='body2'>Savings:</ItemLabel>
        <ItemValue
          variant={variant}
          component='p'
          color={isSmall ? 'grey700' : 'secondary700'}
          fontWeight={isSmall ? 400 : 700}
          data-testid={TestIds.feat.orders.card.savings}
        >
          {calculateOrderDiscount(data)}
        </ItemValue>
      </ItemContainer>
    )
    const orderType = (
      <Box>
        <ItemContainer wrap={isSmall}>
          <ItemLabel variant='body2'>Order type: </ItemLabel>
          <ItemValue
            variant={variant}
            component='p'
            fontWeight={isSmall ? 400 : 700}
            data-testid={TestIds.feat.orders.card.type}
          >
            {getOrderTypeFromDeliveryMode(data.deliveryMode)}
          </ItemValue>
        </ItemContainer>
        {data.deliveryMode === DeliveryMode.Card && data.cardVendor && (
          <ItemLabel
            variant='body2'
            mt={isSmall ? 1 : 0}
            data-testid={TestIds.feat.orders.card.cardVendor}
          >
            {isSmall ? 'Purchase made at:' : ''} {data.cardVendor}
          </ItemLabel>
        )}
      </Box>
    )
    return (
      <PriceWrapper>
        {totalCost}
        {data.discountIncGst > 0 && savings}
        {orderType}
      </PriceWrapper>
    )
  }, [data, isSmall])

  const mappedStatus = React.useMemo(() => {
    const status = mapStatus(data.detailedStatus, data.deliveryMode, data.createdAt)
    return status
  }, [data])

  const productCountLabel = React.useMemo(() => {
    const suffix = data.salesLines.length === 1 ? 'product' : 'products'
    return (
      data.salesLines.length > 0 && (
        <ItemLabel variant='body2' data-testid={TestIds.feat.orders.card.numberOfItems}>
          {data.salesLines.length} {suffix}
        </ItemLabel>
      )
    )
  }, [data])

  return (
    <Card
      variant='outlined'
      key={data.salesId}
      data-id={data.salesId}
      data-testid={TestIds.feat.orders.card.card}
    >
      <CardAction
        tabIndex={-1}
        disableRipple
        onClick={(e) => orderDetailsNavigate(e, '/order/', data.salesId)}
      >
        <TopContainer>
          <CardTitleWrapper>
            <CardTitle
              title={`Order# ${data.salesId}`}
              testId={TestIds.feat.orders.card.title}
              data-id={data.salesId}
            />
            <StatusWrapper>
              {mappedStatus?.display && (
                <StatusChip
                  label={mappedStatus.display}
                  variant='filled'
                  bgColor={mappedStatus.color.bg}
                  textColor={mappedStatus.color.text}
                  data-testid={TestIds.feat.orders.card.status}
                />
              )}
              {productCountLabel}
            </StatusWrapper>
          </CardTitleWrapper>
          {renderOrderPlaced}
          {isSmall && renderCustomerRef}
          <Divider />
        </TopContainer>
        <BottomContainer>
          {renderOrderDetails}
          {!isSmall && renderCustomerRef}
        </BottomContainer>
      </CardAction>
    </Card>
  )
}

export default OrderCard
